// Phase 29.6.2 · SE view — /field/my-day-summary.
// Latest summary card + history list. Empty state when SE hasn't closed
// any day yet. Date picker filters the history list.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, Calendar, Phone, Target, Timer,
  Package, Route as RouteIcon, MapPin, Building2, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtHm } from "@/lib/hours";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const INR = new Intl.NumberFormat("en-IN",
  { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

export default function MyDaySummary() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  // Deep-link from a "Your day summary" notification: ?date=YYYY-MM-DD.
  const [selectedDate, setSelectedDate] = useState(() => searchParams.get("date") || "");
  const [loading, setLoading] = useState(false);

  // ASM manages a team of SEs and doesn't only want their own working
  // summary — they need to check any mapped SE's day too. "" means "my own".
  const isASM = user?.role_code === "ASM";
  const [teamList, setTeamList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");

  useEffect(() => {
    if (!isASM) return;
    // SE,ASM — an ASM can report to another ASM (a sub-ASM structure), so
    // "my team" here must include reportee ASMs too, not just SEs.
    api.get("/users", { params: { role_code: "SE,ASM", status: "active", page_size: 200 } })
      .then(({ data: res }) => setTeamList((res.items || []).filter((u) => u.id !== user?.id)))
      .catch(() => {});
  }, [isASM, user?.id]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (selectedDate) { params.from_date = selectedDate; params.to_date = selectedDate; }
    if (selectedUserId) params.user_id = selectedUserId;
    api.get("/daily-working-summary", { params })
      .then((r) => setRows(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedDate, selectedUserId]);

  const latest = useMemo(() => rows?.[0] || null, [rows]);
  const viewingName = selectedUserId
    ? (teamList.find((t) => t.id === selectedUserId)?.name || "them")
    : null;

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="dws-root">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          {viewingName ? `${viewingName}'s day summary` : "My day summary"}
        </div>
        <h1 className="text-2xl font-extrabold text-neutral-900">
          Daily working summary
        </h1>
      </div>

      {isASM && teamList.length > 0 && (
        <div className="flex items-center gap-2" data-testid="dws-user-picker">
          <Users className="h-4 w-4 text-neutral-500 shrink-0" />
          <Select
            value={selectedUserId || "__mine__"}
            onValueChange={(v) => setSelectedUserId(v === "__mine__" ? "" : v)}
          >
            <SelectTrigger className="h-9 text-sm" data-testid="dws-user-select">
              <SelectValue placeholder="My summary" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__mine__">My summary</SelectItem>
              {teamList.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name || t.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-neutral-500" />
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          data-testid="dws-date-picker"
          className="h-9 max-w-[220px]"
        />
        {selectedDate && (
          <button onClick={() => setSelectedDate("")}
                  className="text-xs text-neutral-500 underline">
            Clear
          </button>
        )}
      </div>

      {loading && !rows && (
        <div className="py-8 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {rows && rows.length === 0 && (
        <Card className="border-dashed border-neutral-300 rounded-2xl"
              data-testid="dws-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            {viewingName
              ? `No day summary yet for ${viewingName}.`
              : "Close your day to see your first summary."}
          </CardContent>
        </Card>
      )}

      {latest && (
        <>
          {/* TC / PC / non-productive */}
          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" /> Calls · {latest.date}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] text-neutral-500">TC</div>
                  <div className="text-2xl font-extrabold"
                       data-testid="dws-tc">{latest.tc}</div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500">PC</div>
                  <div className="text-2xl font-extrabold text-emerald-700"
                       data-testid="dws-pc">{latest.pc}</div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500" title="Non-Productive Calls — visited but no order booked">
                    NPC
                  </div>
                  <div className="text-2xl font-extrabold text-neutral-700">
                    {latest.non_productive_calls}
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-neutral-500 mt-2">
                New outlets today: {latest.new_calls}
              </div>
              <div className="text-[11px] text-neutral-400 mt-0.5">
                NPC = Non-Productive Calls (outlet visited, no order booked)
              </div>
            </CardContent>
          </Card>

          {/* Hours */}
          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <Timer className="h-3.5 w-3.5" /> Time
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] text-neutral-500">Total mkt</div>
                  <div className="text-lg font-bold" data-testid="dws-total-hours">
                    {fmtHm(latest.total_market_hours)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500">In outlet</div>
                  <div className="text-lg font-bold">{fmtHm(latest.in_outlet_time)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500">Travel</div>
                  <div className="text-lg font-bold">{fmtHm(latest.travel_time)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-neutral-100">
                <div>
                  <div className="text-[11px] text-neutral-500">Start call time</div>
                  <div className="text-sm font-semibold" data-testid="dws-first-call">
                    {fmtTime(latest.first_call_ts)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500">End call time</div>
                  <div className="text-sm font-semibold" data-testid="dws-last-call">
                    {fmtTime(latest.last_call_ts)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Secondary */}
          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <Target className="h-3.5 w-3.5" /> Secondary sales
              </div>
              <div className="mt-2 flex items-baseline gap-4">
                <div className="text-2xl font-extrabold"
                     data-testid="dws-secondary-kg">
                  {latest.secondary_kg.toFixed(1)} kg
                </div>
                <div className="text-xs text-neutral-500 font-mono"
                     data-testid="dws-secondary-value">
                  {INR.format(latest.secondary_value_inr)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Focus SKU list */}
          <Card className="border-neutral-200 rounded-2xl"
                data-testid="dws-focus-sku-list">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <Package className="h-3.5 w-3.5" /> Focus SKUs today
              </div>
              {(!latest.focus_sku_sales || latest.focus_sku_sales.length === 0) ? (
                <div className="mt-2 text-xs text-neutral-400">
                  No focus SKUs configured.
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {latest.focus_sku_sales.map((f) => (
                    <div key={f.sku_id}
                         className="flex items-baseline justify-between text-sm">
                      <div className="font-mono text-[11px] text-neutral-500 truncate">
                        {f.sku_id.slice(0, 10)}…
                      </div>
                      <div className="text-neutral-900 font-semibold">
                        {INR.format(f.value_inr)}
                        <span className="text-[11px] text-neutral-500 ml-2 font-normal">
                          {f.qty_kg.toFixed(1)}kg
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Beat coverage — today's planned beat: total calls vs visited (TC) */}
          <Card className="border-neutral-200 rounded-2xl"
                data-testid="dws-coverage">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <RouteIcon className="h-3.5 w-3.5" /> Beat coverage
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="text-2xl font-extrabold" data-testid="dws-beat-visited">
                  {latest.beat_visited_calls_today}
                </div>
                <div className="text-neutral-400 text-lg font-semibold">/</div>
                <div className="text-2xl font-extrabold text-neutral-500" data-testid="dws-beat-total">
                  {latest.beat_total_calls_today}
                </div>
                <div className="text-xs text-neutral-500 ml-2">calls visited</div>
                <div className="ml-auto text-sm font-semibold text-[#6D2C91]">
                  {latest.beat_coverage_pct}%
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beat / Distributor touched today */}
          <Card className="border-neutral-200 rounded-2xl" data-testid="dws-beat-distributor">
            <CardContent className="p-4 space-y-3">
              <div>
                <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" /> Beat(s) visited
                </div>
                <div className="mt-1 text-sm font-medium text-neutral-900">
                  {latest.beat_names?.length ? latest.beat_names.join(", ") : "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5" /> Distributor
                </div>
                <div className="mt-1 text-sm font-medium text-neutral-900">
                  {latest.distributor_names?.length ? latest.distributor_names.join(", ") : "—"}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Past days — click any row to load it into the card above. */}
      {rows && rows.length > 1 && (
        <Card className="border-neutral-200 rounded-2xl" data-testid="dws-history">
          <CardContent className="p-4">
            <div className="text-[11px] text-neutral-500 uppercase tracking-widest">
              Past days — tap to view
            </div>
            <div className="mt-2 space-y-1">
              {rows.slice(1, 30).map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedDate(r.date)}
                  data-testid={`dws-history-row-${r.date}`}
                  className="w-full flex items-baseline justify-between text-sm py-2 border-b border-neutral-100 last:border-b-0 text-left hover:bg-neutral-50 rounded-lg px-1 -mx-1"
                >
                  <div className="font-mono text-neutral-500">{r.date}</div>
                  <div className="text-neutral-800">
                    {r.tc} calls · {(r.secondary_kg || 0).toFixed(1)} kg
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
